import { c as _c } from "react/compiler-runtime";
import { Sparkles, Ticket, TrainPictogram } from '@byway/icons';
import { format, parseISO } from 'date-fns';
import { useTranslations } from 'next-intl';
import { formatDurationHM } from 'utils/DateFormats';
import { ChangeTravelTimesField } from '~/components/TripOverview/transportPage/ChangeTravelTimesField';
import { visuallyHidden } from '~/components/Utilities';
import { getTicketsIncludedKey } from '~/constants/transport-modes';
import type { TripSection } from '~/stores/tpv2/journeyStore';
import type { LegTransformed, Transport } from '~/types/JourneyItem.types';
import { ItineraryCard } from './ItineraryCard';
import type { TagItem } from './ItineraryCard.types';
type HeroRouteCardProps = LegTransformed & {
  section: TripSection;
};
function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'd MMM yy');
  } catch {
    return dateStr;
  }
}
function formatStationLabel(city: string, terminalId: string | null): string {
  return terminalId ? `${city} (${terminalId.split('-')[1]?.toLowerCase() ?? terminalId})` : city;
}
export const HeroRouteCard = t0 => {
  const $ = _c(69);
  const {
    itemId,
    origin,
    destination,
    viaStops,
    startDate,
    departureTimeText,
    arrivalTimeText,
    duration,
    fromTerminalId,
    fromTerminalName,
    toTerminalId,
    toTerminalName,
    images,
    body,
    description,
    interrail,
    channel,
    modeOfTransports,
    section
  } = t0;
  const t = useTranslations("journey");
  const tAria = useTranslations("aria");
  let galleryImages;
  let heroTag;
  let tags;
  if ($[0] !== channel || $[1] !== images || $[2] !== interrail || $[3] !== modeOfTransports || $[4] !== t || $[5] !== viaStops) {
    let t1;
    if ($[9] !== viaStops) {
      t1 = viaStops ?? [];
      $[9] = viaStops;
      $[10] = t1;
    } else {
      t1 = $[10];
    }
    const ticketsIncludedKey = getTicketsIncludedKey(modeOfTransports.filter(Boolean) as Transport[], t1.length > 0);
    let t2;
    if ($[11] !== t) {
      t2 = t("overview_hero_route_tag");
      $[11] = t;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    let t3;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Sparkles size="small" decorative={true} />;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== t2) {
      t4 = {
        label: t2,
        variant: "hero",
        icon: t3
      };
      $[14] = t2;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    heroTag = t4;
    galleryImages = images?.length ? images : null;
    tags = galleryImages ? [] : [heroTag];
    if (interrail || channel === "RAIL") {
      let t5;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <Ticket size="small" decorative={true} />;
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      tags.push({
        label: t(ticketsIncludedKey),
        variant: "positive",
        icon: t5
      });
    }
    $[0] = channel;
    $[1] = images;
    $[2] = interrail;
    $[3] = modeOfTransports;
    $[4] = t;
    $[5] = viaStops;
    $[6] = galleryImages;
    $[7] = heroTag;
    $[8] = tags;
  } else {
    galleryImages = $[6];
    heroTag = $[7];
    tags = $[8];
  }
  let t1;
  if ($[17] !== startDate) {
    t1 = formatDate(startDate);
    $[17] = startDate;
    $[18] = t1;
  } else {
    t1 = $[18];
  }
  const formattedDate = t1;
  let t2;
  if ($[19] !== duration) {
    t2 = duration ? formatDurationHM({
      minutes: duration
    }) : "";
    $[19] = duration;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  const formattedDuration = t2;
  let t3;
  if ($[21] !== fromTerminalId || $[22] !== fromTerminalName || $[23] !== origin) {
    t3 = fromTerminalName ?? formatStationLabel(origin, fromTerminalId);
    $[21] = fromTerminalId;
    $[22] = fromTerminalName;
    $[23] = origin;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  const originStation = t3;
  let t4;
  if ($[25] !== destination || $[26] !== toTerminalId || $[27] !== toTerminalName) {
    t4 = toTerminalName ?? formatStationLabel(destination, toTerminalId);
    $[25] = destination;
    $[26] = toTerminalId;
    $[27] = toTerminalName;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  const destinationStation = t4;
  const hasTravelTimes = Boolean(departureTimeText || arrivalTimeText);
  const bodyText = body?.[0] && "text" in body[0] ? String(body[0].text) : description ?? "";
  let t5;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TrainPictogram size="small" decorative={true} />;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span aria-hidden="true"> → </span>;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== tAria) {
    t7 = tAria("to");
    $[31] = tAria;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const t8 = ` ${t7} `;
  let t9;
  if ($[33] !== t8) {
    t9 = <span className={visuallyHidden}>{t8}</span>;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== destination || $[36] !== origin || $[37] !== t9) {
    t10 = <ItineraryCard.Header icon={t5} title={<>{origin}{t6}{t9}{destination}</>} />;
    $[35] = destination;
    $[36] = origin;
    $[37] = t9;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== galleryImages || $[40] !== heroTag) {
    t11 = galleryImages && <ItineraryCard.Gallery images={galleryImages} tag={<ItineraryCard.Tags tags={[heroTag]} />} />;
    $[39] = galleryImages;
    $[40] = heroTag;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== tags) {
    t12 = <ItineraryCard.Tags tags={tags} />;
    $[42] = tags;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== section || $[45] !== t) {
    t13 = section === "overview" && <ItineraryCard.InfoBanner>{t("overview_choose_times")}</ItineraryCard.InfoBanner>;
    $[44] = section;
    $[45] = t;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== arrivalTimeText || $[48] !== departureTimeText || $[49] !== destination || $[50] !== destinationStation || $[51] !== formattedDate || $[52] !== formattedDuration || $[53] !== hasTravelTimes || $[54] !== itemId || $[55] !== origin || $[56] !== originStation || $[57] !== section || $[58] !== startDate) {
    t14 = section === "transport" && hasTravelTimes && <ItineraryCard.Content><ItineraryCard.TrainTimes date={formattedDate} departureTime={departureTimeText} arrivalTime={arrivalTimeText} originStation={originStation} destinationStation={destinationStation} duration={formattedDuration} /><ChangeTravelTimesField itemId={itemId} startDate={startDate} origin={origin} destination={destination} departureTimeText={departureTimeText} arrivalTimeText={arrivalTimeText} /></ItineraryCard.Content>;
    $[47] = arrivalTimeText;
    $[48] = departureTimeText;
    $[49] = destination;
    $[50] = destinationStation;
    $[51] = formattedDate;
    $[52] = formattedDuration;
    $[53] = hasTravelTimes;
    $[54] = itemId;
    $[55] = origin;
    $[56] = originStation;
    $[57] = section;
    $[58] = startDate;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== bodyText) {
    t15 = bodyText && <ItineraryCard.Body text={bodyText} />;
    $[60] = bodyText;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== t10 || $[63] !== t11 || $[64] !== t12 || $[65] !== t13 || $[66] !== t14 || $[67] !== t15) {
    t16 = <ItineraryCard>{t10}{t11}{t12}{t13}{t14}{t15}</ItineraryCard>;
    $[62] = t10;
    $[63] = t11;
    $[64] = t12;
    $[65] = t13;
    $[66] = t14;
    $[67] = t15;
    $[68] = t16;
  } else {
    t16 = $[68];
  }
  return t16;
};